import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

// Global "mini-player" state — holds whatever video the user has
// chosen to keep watching while they navigate the rest of the app.
//
// Why a top-level context instead of letting each page's player
// render itself? React unmounts the old page's components when the
// route changes, which kills the iframe — and that kills playback.
// By rendering the player ONCE at App.js root and letting pages
// just call `minimize(...)` on it, the iframe survives route
// changes and YouTube/Cloudinary keep playing.
//
// State shape:
//   open      — the floating widget is visible (true) or hidden (false)
//   item      — { id, title, youtube_id?, mp4_url?, archive_id?, poster? }
//   pinned    — user explicitly minimized the modal (vs. just having
//               not opened it yet). Used to decide whether closing the
//               modal should auto-hand-off to the mini-player.
const MiniPlayerCtx = createContext(null);

export function MiniPlayerProvider({ children }) {
  const [item, setItem] = useState(null);   // currently floating media
  const [open, setOpen] = useState(false);
  const [fromPath, setFromPath] = useState(null); // where the user opened the player
  // Queue support — lets MiniPlayer auto-advance to next item after the
  // current one ends (so continuous play works while browsing other pages).
  const itemRef = useRef(null);
  const queueRef = useRef([]);

  const minimize = useCallback((media, mediaQueue) => {
    if (!media) return;
    if (Array.isArray(mediaQueue)) queueRef.current = mediaQueue;
    const payload = {
      id: media.id,
      title: media.title || "",
      subtitle: media.subtitle || media.artist || media.year || "",
      youtube_id: media.youtube_id,
      mp4_url: media.cloudinary_url || media.mp4_url || null,
      archive_id: media.archive_id || null,
      poster: media.poster,
      duration: media.duration || null,
      // Where the user was in the video when they tapped "Keep
      // Playing" / minimized. The mini-player seeks to this on mount
      // so playback continues from the same spot instead of restarting.
      resume_at: Number.isFinite(media.resume_at) ? media.resume_at : 0,
    };
    itemRef.current = payload;
    setItem(payload);
    setOpen(true);
    // Persist a tiny "last played" blob so the BottomNav's Listen
    // slot can offer one-tap resume even after a hard refresh. We
    // intentionally keep this small (no full media URL, no poster
    // longer than ~200 chars) so localStorage stays cheap. The full
    // payload IS what minimize() needs to restore, so we round-trip
    // exactly what we just set as `item` (minus the fat poster).
    try {
      const path = (typeof window !== "undefined") ? window.location.pathname + window.location.search : null;
      setFromPath(path);
      if (typeof window !== "undefined") {
        const last = {
          ...payload,
          poster: (payload.poster || "").slice(0, 220),
          from_path: path,
          saved_at: Date.now(),
        };
        window.localStorage.setItem("hw:last-played", JSON.stringify(last));
        window.dispatchEvent(new CustomEvent("hw:last-played:changed", { detail: last }));
      }
    } catch (_) {}
  }, []);

  // Rehydrate the mini-player from the saved last-played blob. Used
  // by the Listen slot in the BottomNav — taps the slot and the
  // player pops back open playing the same track the user minimized
  // (sermon, gospel song, Bible audio, anything). Returns true if a
  // resume happened so the caller can decide whether to also
  // navigate / show a toast.
  const resume = useCallback(() => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem("hw:last-played");
      if (!raw) return false;
      const last = JSON.parse(raw);
      if (!last || !last.id) return false;
      // Only one of youtube_id or mp4_url is required to actually
      // play. If both are missing the saved blob is too thin to
      // resume from (defensive: shouldn't happen post iter 79).
      if (!last.youtube_id && !last.mp4_url && !last.archive_id) return false;
      setItem({
        id: last.id,
        title: last.title || "",
        subtitle: last.subtitle || "",
        youtube_id: last.youtube_id || null,
        mp4_url: last.mp4_url || null,
        archive_id: last.archive_id || null,
        poster: last.poster || null,
        resume_at: Number.isFinite(last.resume_at) ? last.resume_at : 0,
      });
      setOpen(true);
      setFromPath(last.from_path || null);
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  // Jump to a specific item in the queue and start it from the top.
  const jumpTo = useCallback((next) => {
    if (!next) return;
    const payload = {
      id: next.id,
      title: next.title || "",
      subtitle: next.subtitle || next.artist || next.speaker || next.teacher || "",
      youtube_id: next.youtube_id || null,
      mp4_url: next.cloudinary_url || next.mp4_url || null,
      archive_id: next.archive_id || null,
      poster: next.poster || null,
      resume_at: 0,
      duration: next.duration || null,
    };
    itemRef.current = payload;
    setItem(payload);
    setOpen(true);
  }, []);

  // Advance to the next item in the queue. Wraps around to index 0 at
  // the end so continuous play never stops. Called by MiniPlayer when
  // the current video ends (YouTube state=0, catalog timer, <video> onEnded).
  const advanceQueue = useCallback(() => {
    const list = queueRef.current;
    const current = itemRef.current;
    if (!list || list.length === 0 || !current) return;
    const idx = list.findIndex((t) => t.id === current.id);
    const nextIdx = idx >= list.length - 1 ? 0 : idx + 1;
    jumpTo(list[nextIdx]);
  }, [jumpTo]);

  // Go back to the previous item (wraps to the last). Wired to the OS
  // lock-screen "previous track" control via the Media Session API.
  const previousQueue = useCallback(() => {
    const list = queueRef.current;
    const current = itemRef.current;
    if (!list || list.length === 0 || !current) return;
    const idx = list.findIndex((t) => t.id === current.id);
    const prevIdx = idx <= 0 ? list.length - 1 : idx - 1;
    jumpTo(list[prevIdx]);
  }, [jumpTo]);

  const close = useCallback(() => {
    setItem(null);
    setOpen(false);
    setFromPath(null);
  }, []);

  // Voice Commander fires `hw:stop-all-media` before starting new
  // playback — the minimized video must fully close (not just pause)
  // so its audio can't keep running under the new video.
  React.useEffect(() => {
    const onStopAll = () => close();
    window.addEventListener("hw:stop-all-media", onStopAll);
    return () => window.removeEventListener("hw:stop-all-media", onStopAll);
  }, [close]);

  const value = useMemo(() => ({ item, open, fromPath, minimize, resume, close, advanceQueue, previousQueue }), [item, open, fromPath, minimize, resume, close, advanceQueue, previousQueue]);
  return <MiniPlayerCtx.Provider value={value}>{children}</MiniPlayerCtx.Provider>;
}

export function useMiniPlayer() {
  return useContext(MiniPlayerCtx) || { item: null, open: false, fromPath: null, minimize: () => {}, resume: () => false, close: () => {}, advanceQueue: () => {}, previousQueue: () => {} };
}
